import { useEffect, useState } from "react";
import { Clock } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shared hook: fetches the live launch-offer once so every buying surface
// (dashboard banner, pricing modal, /premium, /go-premium) shows the SAME offer.
export const useLaunchOffer = () => {
  const [offer, setOffer] = useState(null);
  useEffect(() => {
    let ok = true;
    fetch(`${API_URL}/api/premium-plan/launch-offer`)
      .then((r) => r.json())
      .then((d) => { if (ok) setOffer(d); })
      .catch(() => {});
    return () => { ok = false; };
  }, []);
  const pct = offer?.discount_percent || 25;
  const mode = offer?.mode || 'one_time';
  const firstCyclePrice = (planId) => offer?.plans?.[planId]?.first_cycle_price;
  const configured = (planId) => !!offer?.plans?.[planId]?.configured;
  const anyConfigured = !!offer && Object.values(offer.plans || {}).some((p) => p.configured);
  // one_time launch mode: discount applies to ALL plans via a one-time charge
  // (no Razorpay Offer/offer_id needed). offer.active already reflects the time window.
  const oneTime = !!offer?.active && mode === 'one_time';
  const showDiscount = (planId) => oneTime || configured(planId);
  const live = !!offer?.active && (oneTime || anyConfigured);
  return { offer, active: !!offer?.active, live, oneTime, mode, anyConfigured, configured, showDiscount, pct, endsAt: offer?.ends_at, firstCyclePrice };
};

function useTimeLeft(endsAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!endsAt) return null;
  const diff = new Date(endsAt).getTime() - now;
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s };
}

// Slim urgency countdown. variant: "light" (on dark bg) | "dark" (on light bg)
export const OfferCountdown = ({ endsAt, variant = "light", className = "", showIcon = true }) => {
  const t = useTimeLeft(endsAt);
  if (!t) return null;
  const isLight = variant === "light";
  const cell = isLight ? "bg-white/15 text-white" : "bg-[#1A362D] text-white";
  const label = isLight ? "text-white/70" : "text-stone-500";
  const Unit = ({ v, u }) => (
    <div className="flex flex-col items-center">
      <span className={`${cell} font-mono font-bold text-sm md:text-base rounded-md px-2 py-1 min-w-[34px] text-center tabular-nums`}>
        {String(v).padStart(2, "0")}
      </span>
      <span className={`${label} text-[10px] mt-0.5 uppercase tracking-wide`}>{u}</span>
    </div>
  );
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="offer-countdown">
      {showIcon && <Clock size={16} weight="fill" className={isLight ? "text-[#F5D77E]" : "text-[#9C302B]"} />}
      <div className="flex items-center gap-1.5">
        {t.d > 0 && <Unit v={t.d} u="days" />}
        <Unit v={t.h} u="hrs" />
        <Unit v={t.m} u="min" />
        <Unit v={t.s} u="sec" />
      </div>
    </div>
  );
};

export default OfferCountdown;
